import { useState } from "react";
import { motion } from "framer-motion";
import {
  User, Bell, Link2, CreditCard, Users, Shield, Save,
  Zap, Check, MessageSquare, Phone, Globe
} from "lucide-react";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "team", label: "Team", icon: Users },
  { id: "integrations", label: "Integrations", icon: Link2 },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "billing", label: "Billing", icon: CreditCard },
];

const TEAM_MEMBERS = [
  { name: "Rajesh Mehta", role: "Admin", email: "rajesh@eliteestate.com", avatar: "RM", status: "active" },
  { name: "Priya Kapoor", role: "Sales Manager", email: "priya@eliteestate.com", avatar: "PK", status: "active" },
  { name: "Rohan Mehta", role: "Sales Executive", email: "rohan@eliteestate.com", avatar: "RM2", status: "active" },
  { name: "Amit Desai", role: "Sales Executive", email: "amit@eliteestate.com", avatar: "AD", status: "active" },
  { name: "Sneha Rao", role: "Marketing", email: "sneha@eliteestate.com", avatar: "SR", status: "inactive" },
];

const INTEGRATIONS = [
  { name: "Meta Ads", desc: "Connect Meta Business Manager for lead capture", icon: Globe, connected: true, color: "text-blue-600 bg-blue-50" },
  { name: "Google Ads", desc: "Sync Google Ads leads and conversions", icon: Globe, connected: true, color: "text-red-600 bg-red-50" },
  { name: "WhatsApp Business", desc: "Connect WhatsApp Business API for messaging", icon: MessageSquare, connected: true, color: "text-green-600 bg-green-50" },
  { name: "AI Calling (PropVision)", desc: "AI-powered outbound calling agent", icon: Phone, connected: true, color: "text-purple-600 bg-purple-50" },
  { name: "Google Sheets", desc: "Export leads and data to Google Sheets", icon: Globe, connected: false, color: "text-slate-600 bg-slate-50" },
  { name: "Zapier", desc: "Automate workflows with 5000+ apps", icon: Zap, connected: false, color: "text-orange-600 bg-orange-50" },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({
    newLead: true, aiCallComplete: true, siteVisit: true, booking: true,
    campaignAlert: false, weeklyReport: true,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="settings-page">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Settings</h2>
        <p className="text-slate-500 text-sm mt-0.5">Manage your PropVision account and preferences</p>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Tab Navigation */}
        <div className="w-full md:w-48 shrink-0">
          <nav className="space-y-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`settings-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6">
          {/* Profile */}
          {activeTab === "profile" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="profile" data-testid="profile-tab">
              <h3 className="font-heading font-semibold text-slate-900 mb-5">Company Profile</h3>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">RM</div>
                <div>
                  <p className="font-semibold text-slate-900">Rajesh Mehta</p>
                  <p className="text-sm text-slate-500">Founder & Chairman · Elite Estate</p>
                  <button className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1">Change Photo</button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { label: "Company Name", value: "Elite Estate" },
                  { label: "Your Name", value: "Rajesh Mehta" },
                  { label: "Email", value: "rajesh@eliteestate.com" },
                  { label: "Phone", value: "+91 98765 00001" },
                  { label: "City", value: "Mumbai" },
                  { label: "Website", value: "www.eliteestate.com" },
                ].map(field => (
                  <div key={field.label}>
                    <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">{field.label}</label>
                    <input
                      type="text"
                      defaultValue={field.value}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1.5">About Company</label>
                <textarea
                  defaultValue="Elite Estate is a premium real estate brand specializing in high-quality residential properties across India."
                  rows={3}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 resize-none"
                />
              </div>
            </motion.div>
          )}

          {/* Team */}
          {activeTab === "team" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="team" data-testid="team-tab">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-semibold text-slate-900">Team Members</h3>
                <button className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors">
                  <Users className="w-3.5 h-3.5" /> Invite Member
                </button>
              </div>
              <div className="space-y-3">
                {TEAM_MEMBERS.map(member => (
                  <div key={member.email} className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {member.avatar.slice(0, 2)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{member.name}</p>
                      <p className="text-xs text-slate-500">{member.email}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${member.status === "active" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {member.status}
                    </span>
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{member.role}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Integrations */}
          {activeTab === "integrations" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="integrations" data-testid="integrations-tab">
              <h3 className="font-heading font-semibold text-slate-900 mb-5">Integrations</h3>
              <div className="space-y-3">
                {INTEGRATIONS.map(intg => (
                  <div key={intg.name} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${intg.color}`}>
                      <intg.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{intg.name}</p>
                      <p className="text-xs text-slate-500">{intg.desc}</p>
                    </div>
                    <button
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${intg.connected ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      data-testid={`integration-${intg.name.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {intg.connected ? (
                        <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>
                      ) : "Connect"}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Notifications */}
          {activeTab === "notifications" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="notifications" data-testid="notifications-tab">
              <h3 className="font-heading font-semibold text-slate-900 mb-5">Notification Preferences</h3>
              <div className="space-y-4">
                {[
                  { key: "newLead", label: "New Lead Captured", desc: "Notify when a new lead enters the pipeline" },
                  { key: "aiCallComplete", label: "AI Call Completed", desc: "Notify when an AI outbound call finishes" },
                  { key: "siteVisit", label: "Site Visit Confirmed", desc: "Notify when a site visit is scheduled" },
                  { key: "booking", label: "Booking Completed", desc: "Notify when a booking is confirmed" },
                  { key: "campaignAlert", label: "Campaign Alert", desc: "Notify when campaign CPL exceeds threshold" },
                  { key: "weeklyReport", label: "Weekly Report", desc: "Send weekly summary every Monday morning" },
                ].map(notif => (
                  <div key={notif.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{notif.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{notif.desc}</p>
                    </div>
                    <button
                      onClick={() => setNotifications(n => ({ ...n, [notif.key]: !n[notif.key] }))}
                      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${notifications[notif.key] ? "bg-blue-600" : "bg-slate-200"}`}
                      data-testid={`notif-toggle-${notif.key}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${notifications[notif.key] ? "left-6" : "left-1"}`} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Billing */}
          {activeTab === "billing" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="billing" data-testid="billing-tab">
              <h3 className="font-heading font-semibold text-slate-900 mb-5">Billing & Plan</h3>
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white mb-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-blue-200 text-xs font-semibold uppercase tracking-wide">Current Plan</p>
                    <p className="text-2xl font-heading font-bold">Growth</p>
                  </div>
                  <Zap className="w-8 h-8 text-blue-300" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[["5 Users", "Seats"], ["2,000", "Leads/mo"], ["5", "Properties"]].map(([v, l]) => (
                    <div key={l} className="bg-white/10 rounded-xl p-2.5 text-center">
                      <p className="font-bold text-lg">{v}</p>
                      <p className="text-blue-200 text-xs">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-slate-800">Next billing date</p>
                  <p className="font-bold text-slate-900">Feb 1, 2024</p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-slate-600 text-sm">Amount due</p>
                  <p className="font-heading text-xl font-bold text-slate-900">₹12,999</p>
                </div>
              </div>
              <button className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm">
                <Shield className="w-4 h-4" /> Upgrade to Enterprise
              </button>
            </motion.div>
          )}

          {/* Save Button (for profile/notifications) */}
          {(activeTab === "profile" || activeTab === "notifications") && (
            <div className="mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={handleSave}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${saved ? "bg-emerald-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                data-testid="save-settings-btn"
              >
                {saved ? <><Check className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Changes</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
